/**
 * /xerf-vs-morpheus8-thermage-ultherapy (indexable, organic).
 *
 * An honest comparison page for the "XERF vs ..." searches. It names devices Lakewood does not own, says so
 * plainly, and makes no superiority or percentage claims. FAQ text comes from shared/skinTighteningCompareFaqs.ts
 * so the visible questions and the FAQPage JSON-LD are the same words. The XERF page itself is not touched.
 */
import { Link } from "wouter";
import { useEffect } from "react";
import { trackViewContent } from "@/lib/gtm";
import TrademarkNote from "@/components/TrademarkNote";
import { SKIN_TIGHTENING_COMPARE_FAQS } from "@shared/skinTighteningCompareFaqs";
import { ArticleSection, C, FaqList, PageHero, RelatedLinks, ServicePageShell } from "./AdditiveSeoPages";

type Row = { name: string; maker: string; how: string; needles: string; bestFor: string; here: string };

const ROWS: Row[] = [
  {
    name: "XERF",
    maker: "Cynosure Lutronic",
    how: "Multifrequency monopolar radiofrequency (6.78 MHz and 2 MHz) with surface cooling",
    needles: "None",
    bestFor: "Early looseness of the face, jawline, under the chin and neck",
    here: "Offered",
  },
  {
    name: "Thermage",
    maker: "Solta Medical",
    how: "Monopolar radiofrequency",
    needles: "None",
    bestFor: "Same class of treatment as XERF: skin firmness on the face and body",
    here: "Not offered",
  },
  {
    name: "Ultherapy",
    maker: "Merz Aesthetics",
    how: "Micro-focused ultrasound",
    needles: "None",
    bestFor: "Lift and tightening of the brow, jawline and neck",
    here: "Not offered. We offer HIFU, a different focused-ultrasound device",
  },
  {
    name: "Morpheus8",
    maker: "InMode",
    how: "Radiofrequency delivered through microneedles (RF microneedling)",
    needles: "Yes",
    bestFor: "Skin texture, acne scars and mild-to-moderate laxity",
    here: "Not offered. We offer a different RF microneedling device",
  },
];

export function SkinTighteningCompared() {
  useEffect(() => { trackViewContent("xerf-vs-morpheus8-thermage-ultherapy"); }, []);
  return (
    <ServicePageShell stickyLabel="Skin Tightening Consultation">
      <PageHero
        eyebrow="Skin tightening compared"
        title="XERF vs Morpheus8, Thermage and Ultherapy in Dallas"
        intro="These four treatments get compared constantly, but they are not interchangeable. They use different energy, reach different depths, and suit different concerns. Here is a straight comparison from Dr. Rajan Kohli, MD, including which of them Lakewood Med Spa does and does not offer."
      />
      <div className="container" style={{ maxWidth: 980 }}>
        <ArticleSection title="The short answer">
          <p>There is no single best skin tightening device. Radiofrequency (RF) treatments like XERF and Thermage heat the deeper layers of skin without needles. Ultrasound treatments like Ultherapy and HIFU use focused sound energy instead. Morpheus8 sends RF through microneedles, which makes it as much a texture and scar treatment as a tightening one.</p>
          <p>Which one fits depends on what is changing in your face, how much looseness you have, and how you feel about needles and recovery. No comparison on a web page can replace being examined.</p>
        </ArticleSection>

        <ArticleSection title="Side by side">
          <div style={{ overflowX: "auto", marginTop: ".6rem" }}>
            <table style={{ borderCollapse: "collapse", minWidth: 720, width: "100%", fontSize: ".92rem" }}>
              <thead>
                <tr style={{ background: C.warm, textAlign: "left" }}>
                  {["Treatment", "How it works", "Needles", "Often considered for", "At Lakewood"].map((h) => (
                    <th key={h} scope="col" style={{ border: `1px solid ${C.border}`, color: C.dark, padding: ".7rem .8rem" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((r) => (
                  <tr key={r.name} style={{ background: C.white, verticalAlign: "top" }}>
                    <th scope="row" style={{ border: `1px solid ${C.border}`, color: C.dark, fontFamily: "'Playfair Display',serif", padding: ".7rem .8rem", textAlign: "left" }}>
                      {r.name}
                      <span style={{ color: C.muted, display: "block", fontFamily: "'Lato',sans-serif", fontSize: ".78rem", fontWeight: 400 }}>{r.maker}</span>
                    </th>
                    <td style={{ border: `1px solid ${C.border}`, color: C.muted, padding: ".7rem .8rem" }}>{r.how}</td>
                    <td style={{ border: `1px solid ${C.border}`, color: C.muted, padding: ".7rem .8rem" }}>{r.needles}</td>
                    <td style={{ border: `1px solid ${C.border}`, color: C.muted, padding: ".7rem .8rem" }}>{r.bestFor}</td>
                    <td style={{ border: `1px solid ${C.border}`, color: C.dark, fontWeight: 700, padding: ".7rem .8rem" }}>{r.here}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: ".85rem", marginTop: ".9rem" }}>Descriptions are general. Each device is made by the company shown and is not affiliated with Lakewood Med Spa. Results vary and none of these treatments is guaranteed to give a particular outcome.</p>
        </ArticleSection>

        <ArticleSection title="XERF vs Thermage: the same class of treatment">
          <p>Both are needle-free monopolar radiofrequency treatments, so the experience and the mechanism are similar: heat is delivered into the deeper layers while the surface is cooled. XERF is a multifrequency system that pairs 6.78 MHz and 2 MHz energy. We do not offer Thermage and we do not claim XERF beats it. If you are choosing between needle-free RF options, the practical questions are who is planning your treatment, how many sessions the plan needs, and the total price.</p>
        </ArticleSection>

        <ArticleSection title="XERF vs Ultherapy: heat versus sound">
          <p>The difference is the energy. XERF uses radiofrequency heat; Ultherapy uses micro-focused ultrasound. Neither is automatically stronger. We do not offer Ultherapy. For people whose concern suits focused ultrasound, Dr. Kohli offers <Link href="/hifu">HIFU</Link>, a different device that works through the same kind of energy.</p>
        </ArticleSection>

        <ArticleSection title="XERF vs Morpheus8: needle-free versus microneedling">
          <p>Morpheus8 is a branded RF microneedling device, so its energy goes in through needles. That makes it a stronger conversation for texture, pores and acne scars, and it usually brings more redness and swelling afterward than a needle-free treatment. XERF is the needle-free choice for early looseness. We do not offer Morpheus8; we offer <Link href="/rf-microneedling">RF microneedling</Link> with a different device when texture or scarring is the main concern.</p>
        </ArticleSection>

        <ArticleSection title="What XERF costs and what to expect here">
          <p>Introductory XERF pricing at Lakewood Med Spa is $1,250 for one session or $3,200 for a series of three. Many people plan one to two sessions about four weeks apart, sometimes up to three. XERF has no needles, and most people have minimal planned downtime. The consultation is complimentary. Read the full details on the <Link href="/xerf-skin-tightening">XERF skin tightening page</Link>.</p>
        </ArticleSection>

        <ArticleSection title="When none of them is the right answer">
          <p>If you have a significant amount of excess skin, no energy-based device will lift it the way surgery can. Dr. Kohli will tell you that at your consultation and can refer you to a surgeon. For the full set of non-surgical options, see our <Link href="/non-surgical-face-lift">non-surgical face lift</Link> page.</p>
        </ArticleSection>

        <section aria-label="Skin tightening comparison frequently asked questions" style={{ background: C.warm, border: `1px solid ${C.border}`, borderRadius: 18, margin: "1rem 0 3.5rem", padding: "clamp(1.3rem,4vw,2.4rem)" }}>
          <p style={{ color: C.accent, fontFamily: "'Lato',sans-serif", fontSize: ".74rem", fontWeight: 800, letterSpacing: ".15em", margin: "0 0 .45rem", textTransform: "uppercase" }}>Frequently asked questions</p>
          <h2 style={{ color: C.dark, fontFamily: "'Playfair Display',serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", margin: "0 0 1rem" }}>XERF, Morpheus8, Thermage and Ultherapy</h2>
          <FaqList faqs={SKIN_TIGHTENING_COMPARE_FAQS.map(({ q, a }) => ({ question: q, answer: a }))} />
        </section>
        <div style={{ margin: "0 0 2.5rem" }}><TrademarkNote brands={["Morpheus8", "Thermage", "Ultherapy"]} /></div>
      </div>
      <RelatedLinks links={[
        { label: "XERF Skin Tightening", href: "/xerf-skin-tightening" },
        { label: "HIFU Skin Tightening", href: "/hifu" },
        { label: "RF Microneedling", href: "/rf-microneedling" },
        { label: "Non-Surgical Face Lift", href: "/non-surgical-face-lift" },
      ]} />
    </ServicePageShell>
  );
}
